import { useState, useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import { Row, Col, Tab, Tabs, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { confirmAlert } from 'react-confirm-alert';

//components
import withLocalization from '~/hoc/withLocalization';
import EditNotificationModal from '../../ModuleSettings/EditNotifications/EditNotificationModal';
import TableWidget from '../TableWidget';

//elements
import LoadingSpinner from '~/elements/LoadingSpinner';
import Select from '~/elements/Select';

//utils
import { NOTIFICATION_CATEGORIES, NOTIFICATION_CATEGORY_LABELS } from '~/utils/constants';
import { formatKey } from '~/utils/commonMethods';

const NotificationMessages = ({ t, notificationStore, commonStore }) => {
    const { masterNotifications, clientMessages, loading, lastInteractedId } = notificationStore;
    const [selectedItem, setSelectedItem] = useState(null);
    const [activeCategory, setActiveCategory] = useState('');
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState(null);

    const statusFilterOptions = [
        { value: 'batch', label: t('Batch') },
        { value: 'custom', label: t('Custom') },
        { value: 'default', label: t('Default') },
    ];

    useEffect(() => {
        Promise.all([
            notificationStore.loadMasterNotifications(true),
            notificationStore.loadClientMessages(),
        ]);
        return () => notificationStore.clearLastInteractedId();
    }, []);

    const categories = NOTIFICATION_CATEGORIES.filter(
        (category) => masterNotifications[category] && masterNotifications[category].length > 0
    );

    const getDisabledReason = (n) => {
        if (!n.is_active) return t('This notification is currently inactive and cannot be customized.');
        if (n.is_batch_notification) return t('This notification can only be managed by the Super Admin.');
        if (!n.is_message_customizable) return t('You do not have permission to customize this notification message.');
        return null;
    };

    useEffect(() => {
        if (categories.length === 0) return;
        if (!categories.includes(activeCategory)) setActiveCategory(categories[0]);
    }, [categories.join(','), activeCategory]);

    const handleCategoryChange = (category) => {
        setActiveCategory(category);
        setSearch('');
        setStatusFilter(null);
    };

    const getMessageStatusKey = (item) => (item.is_batch_notification ? 'batch' : clientMessages[item.id] ? 'custom' : 'default');

    const matchesSearch = (item) => {
        if (!search.trim()) return true;
        const term = search.trim().toLowerCase();
        return formatKey(item.key).toLowerCase().includes(term) || (item.description || '').toLowerCase().includes(term);
    };

    const matchesStatusFilter = (item) => !statusFilter || getMessageStatusKey(item) === statusFilter.value;

    const getInitialMessages = (item) => {
        const custom = clientMessages[item.id];
        if (!custom) return null;
        return {
            push: custom.push ? { title: custom.push.title, message: custom.push.message } : null,
            email: custom.email ? { title: custom.email.title, message: custom.email.message } : null,
        };
    };

    const handleSaveMessages = async (pushData, emailData) => {
        await notificationStore.upsertClientMessage(selectedItem.id, { push: pushData, email: emailData });
        await notificationStore.loadClientMessages();
    };

    const handleRevert = (item) => {
        confirmAlert({
            title: t('Use Default Message'),
            message: t('Are you sure you want to remove the custom message and send the default system message instead?'),
            buttons: [
                {
                    label: t('Yes'),
                    onClick: async () => {
                        try {
                            const res = await notificationStore.deleteClientMessage(item.id);
                            await notificationStore.loadClientMessages();
                            commonStore.addNotification(res.message || t('Default system message will now be used for this notification'), null, 'success');
                        } catch (e) {
                            commonStore.addNotification(e?.message || t('Something went wrong'), null, 'error');
                        }
                    },
                },
                { label: t('No'), onClick: () => { } },
            ],
        });
    };

    const getRowClassName = (item) => `${item.id === lastInteractedId ? 'interacted-row' : ''} ${getDisabledReason(item) ? 'opacity-50' : ''}`.trim();

    const handleOpenNotification = (categoryNotifications, idx) => {
        const item = categoryNotifications[idx];
        const disabledReason = getDisabledReason(item);
        if (disabledReason) {
            commonStore.addNotification(disabledReason, null, 'info');
            return;
        }
        notificationStore.setLastInteractedId(item.id);
        setSelectedItem(item);
    };

    const handleDeleteMessage = (categoryNotifications, idx) => {
        const item = categoryNotifications[idx];
        if (!clientMessages[item.id]) return;
        handleRevert(item);
    };

    if (loading && Object.keys(masterNotifications).length === 0) return <LoadingSpinner />;

    return (
        <>
            <Row className="align-items-center">
                <Col xs={12}>
                    <label className="control-label font-size-15 font-color-primary">
                        {t('Notification')} {t('Messages')}
                    </label>
                </Col>
            </Row>
            <Row className="mb-2 mt-1">
                <Col xs={12}>
                    <p>
                        {t('Note')}** {t('Create and manage custom notification messages for your organization. If no custom message is configured, the default system message will be used.')}
                    </p>
                </Col>
            </Row>

            <Row className="mb-2 align-items-center">
                <Col md={7}>
                    <div className="d-flex conversation-search">
                        <input
                            type="text"
                            className="conversation-search-input"
                            placeholder={t('Search notifications')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <i className="conversation-search-icon fa fa-search" />
                    </div>
                </Col>
                <Col md={5}>
                    <Select
                        options={statusFilterOptions}
                        value={statusFilter}
                        onChange={(opt) => setStatusFilter(opt)}
                        placeholder={t('Filter by message type')}
                        isClearable
                    />
                </Col>
            </Row>

            <Tabs activeKey={activeCategory} onSelect={handleCategoryChange} id="NotificationMessagesTabs" className="mt-2">
                {categories.map((category) => {
                    const categoryNotifications = (masterNotifications[category] || []).filter((n) => matchesSearch(n) && matchesStatusFilter(n));
                    const tableData = categoryNotifications.map((n) => {
                        const disabledReason = getDisabledReason(n);
                        const statusKey = getMessageStatusKey(n);
                        return {
                            ...n,
                            displayName: disabledReason ? (
                                <OverlayTrigger placement="top" overlay={<Tooltip id={`disabled-tooltip-${n.id}`}>{disabledReason}</Tooltip>}>
                                    <span>{formatKey(n.key)}</span>
                                </OverlayTrigger>
                            ) : (<span>{formatKey(n.key)}</span>),
                            descriptionText: n.description || '-',
                            messageStatus: statusFilterOptions.find((o) => o.value === statusKey)?.label,
                            isNotDeletable: !!disabledReason || !clientMessages[n.id],
                        };
                    });

                    return (
                        <Tab key={category} eventKey={category} title={t(NOTIFICATION_CATEGORY_LABELS[category])}>
                            <div className="custom-card mt-3 position-relative">
                                {loading &&
                                    <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-white bg-opacity-75 z-1">
                                        <LoadingSpinner noMargin />
                                    </div>
                                }
                                <TableWidget
                                    headers={['Notification', 'Description', 'Message']}
                                    metaData={[
                                        { field: 'displayName', type: 'text', html: true, thStyle: { textAlign: 'left', minWidth: '160px' }, tdStyle: { textAlign: 'left' } },
                                        { field: 'descriptionText', type: 'text', thStyle: { textAlign: 'left' }, tdStyle: { textAlign: 'left', color: '#6c757d', fontSize: '13px' } },
                                        { field: 'messageStatus', type: 'text' },
                                    ]}
                                    data={tableData}
                                    onChange={() => { }}
                                    getRowClassName={getRowClassName}
                                    customFormHandler={(idx) => handleOpenNotification(categoryNotifications, idx)}
                                    customDeleteHandler={(idx) => handleDeleteMessage(categoryNotifications, idx)}
                                />
                            </div>
                        </Tab>
                    );
                })}
            </Tabs>

            <EditNotificationModal
                show={!!selectedItem}
                onHide={() => setSelectedItem(null)}
                notification={selectedItem}
                hideSettings
                initialMessages={selectedItem ? getInitialMessages(selectedItem) : null}
                onSaveMessages={handleSaveMessages}
            />
        </>
    );
};

export default inject('notificationStore', 'commonStore')(withLocalization(observer(NotificationMessages)));
